/**
 * /use-cases/design-system-tracking — designer workflow for keeping a
 * design system coherent across multiple product surfaces, with AI
 * agents reading principles before suggesting changes.
 */
import { MarketingShell } from '../MarketingShell';

export function DesignSystemTracking() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/use-cases/design-system-tracking"
      documentTitle="Use case: Design system tracking · Draftlize"
      documentDescription="How product designers keep design systems coherent across surfaces using Draftlize. Principles, tokens, component rationales as cards the AI agent reads before suggesting changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Use case · Design system tracking</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Design systems that<br/>
            <em>keep their reasons.</em>
          </h1>
          <p className="lede">
            For product designers running a design system across 3+ surfaces. Color principles, type scales, component rationales as cards the agent reads before suggesting any change.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">The problem with <em>Figma libraries + a Notion principles doc.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Rationales evaporate</h3>
            <p>You picked sentence case for buttons. 8 months later you can't remember why. New designer suggests title case. Without the rationale, you're re-deciding from scratch every quarter.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Cross-surface drift</h3>
            <p>Mobile uses sentence case, web uses title case, email templates use ALL CAPS. Nobody noticed until brand audit. Each surface evolved without seeing the others.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI design conversations forget by next sprint</h3>
            <p>You explain to Claude "we use semantic-blue for links, not brand-blue, because of accessibility contrast." Three weeks later, new chat, Claude suggests brand-blue links again.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">What changes <em>in Draftlize.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each principle is an addressable card</h3>
            <p><code>c-03 decision: semantic-blue for links, not brand-blue</code> + <code>c-04 observation: brand-blue fails WCAG AA on warm-paper bg</code> as the <code>→supports</code> evidence. Both citable forever.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Cross-surface contradictions surface as gaps</h3>
            <p>You write a mobile principle that conflicts with a web principle. Draftlize's <code>conflict_detect</code> files a <code>gap_kind=conflict</code> card showing both, before either ships.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Agent reads principles before suggesting changes</h3>
            <p>New conversation: "design a new error state." Agent reads <code>c-03</code> (link color), <code>c-08</code> (sentence case principle), <code>c-15</code> (error tone guideline) as ambient context. Suggestions arrive already aligned with the system.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">A real day, <em>compressed.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">9:00</span>
            <h3 className="serif">Sweep new components</h3>
            <p>Open Draftlize, agent surfaces 3 cards flagged stale (you changed the color principle yesterday). 5 min to update the affected component specs.</p>
          </div>
          <div className="problem-col">
            <span className="num">11:30</span>
            <h3 className="serif">Cross-surface audit</h3>
            <p>New <code>gap_kind=conflict</code>: mobile picked sentence case, email template proposed title case. Decide which is right (sentence case won), update the email principle card.</p>
          </div>
          <div className="problem-col">
            <span className="num">15:00</span>
            <h3 className="serif">Design review prep</h3>
            <p>Agent reads all your design principle cards and generates a `design-doc` view filtered to the components you're reviewing today. 10 min review, no surprises about precedent.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          "Caught a sentence-case violation across 3 surfaces in 5 seconds. Used to take a 30-min audit before each design review."
        </blockquote>
        <cite>— Diego, Staff Product Designer, fintech (Draftlize dogfood)</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Try it on <em>your design system.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Seed Draftlize with your current design principles in 30-60 min. By the third review, the agent will be aligning suggestions to your system without you re-explaining anything.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
